import React, {useEffect, useRef} from 'react'
import {observer} from 'mobx-react'
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {Button, IconButton, Select} from "@mui/material";
import ConditionalComponent from "../Shared/ConditionalComponent";
import {languageEnum, procedureNodeTypeEnum, procedureTypeEnum} from "../../serverClientShared/sharedEnums";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import separatorImg from '../../assets/img/breadcrumb-separator.svg';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import {useStores} from "../../Hooks";
import SidebarOpenIcon from '../../UniqueIcons/close-sidebar-icon.png';
import SidebarCloseIcon from '../../UniqueIcons/open-sidebar-icon.png';
import EllipsisContainer from "../Shared/EllipsisContainer";
import Utility from "../../Utility";
import {isTablet, isIOS} from "react-device-detect";

const isIphone = isIOS && !isTablet;

const ProcedureHeader = observer(({isPreview, withFullscreen, fullscreenRefElement}) => {
    const {procedureStore, generalStore} = useStores();

    const headerRef = useRef(null);
    let sidebar = isPreview ? procedureStore.previewSidebar : procedureStore.sidebar;
    const currentNode = (isPreview ? procedureStore.currentPreviewedNode : procedureStore.currentNode) || {};
    const isEndNode = !!currentNode && currentNode.NodeType === procedureNodeTypeEnum.END;
    const isStartNode = !!currentNode && currentNode.NodeType === procedureNodeTypeEnum.START;

    if (isEndNode) {
        sidebar = false
    }

    const procedure = isPreview ? procedureStore.currentPreviewedProcedure : procedureStore.currentProcedure;
    const [userSelectedLanguage, setUserSelectedLanguage] = React.useState(procedure.selectedLanguage);

    function toggleSidebar() {
        if (isPreview) {
            procedureStore.previewSidebar = !procedureStore.previewSidebar;
        } else {
            procedureStore.sidebar = !procedureStore.sidebar;
        }
    }

    function renderBreadCrumbs(procedure) {
        if (!procedureStore.fetchedProceduresMap.size || !procedureStore.innerProcedureParentProcedureIds?.length) {
            return <>{procedure.ProcedureName}</>
        }
        const procedures = [];
        procedureStore.innerProcedureParentProcedureIds.forEach(procedureId => {
            const innerProcedure = procedureStore.fetchedProceduresMap.get(procedureId);
            procedures.push(innerProcedure);
        });
        const currentProcedure = isPreview ? procedureStore.currentPreviewedProcedure : procedureStore.currentProcedure;
        procedures.push(currentProcedure);
        return <Breadcrumbs separator={<img src={separatorImg} alt=''/>} maxItems={8}>
            {procedures.map((procedure, index) => (
                <EllipsisContainer
                    value={procedure.ProcedureName}
                    key={procedure.ProcedureIdentifier}
                >
                    <div
                        className='breadcrumb'
                        onClick={() => currentProcedure._id !== procedure._id && procedureStore.jumpToAncestorProcedure(index, isPreview)}
                    >
                        {procedure.ProcedureName}
                    </div>
                </EllipsisContainer>
            ))}
        </Breadcrumbs>
    }

    function toggleSupportingText() {
        procedureStore.showSupportingText = !procedureStore.showSupportingText
    }

    const isRegularProcedure = procedure.ProcedureType === procedureTypeEnum.REGULAR;

    const availableLanguages = isPreview ? procedureStore.availablePreviewedNodeLanguages : procedureStore.availableNodeLanguages;

    const handleChangeSelectedLanguage = e => {
        const newLang = e.target.value;
        setUserSelectedLanguage(newLang)
        if (isPreview) {
            procedureStore.currentPreviewedProcedure.selectedLanguage = newLang;
        } else {
            procedureStore.currentProcedure.selectedLanguage = newLang;
            procedureStore.currentSession.ViewedLanguage = newLang;
        }
        if (generalStore.loggedIn) {
            generalStore.updateUser({
                Email: generalStore.userDetails.Email,
                PreferredLanguage: newLang
            }).then(success => {
                if (success) {
                    generalStore.userDetails.PreferredLanguage = newLang;
                }
            });
        }
        procedureStore.escapeAndSpeakDescription(isPreview);
    }

    useEffect(() => {
        if (availableLanguages.length > 0) {
            const isLangAvailableInProcedure = !!availableLanguages.filter(lang => languageEnum[lang] === userSelectedLanguage).length
            let newLang = procedure.DefaultLanguage
            if (isLangAvailableInProcedure) {
                newLang = userSelectedLanguage;
            }
            if (isPreview) {
                procedureStore.currentPreviewedProcedure.selectedLanguage = newLang;
            } else {
                procedureStore.currentProcedure.selectedLanguage = newLang;
                procedureStore.currentSession.ViewedLanguage = newLang;
            }

            setUserSelectedLanguage(newLang)
        }
        // eslint-disable-next-line
    }, [procedure]);

    return (

        <div className={"procedure-header" + (generalStore.isInFullscreen ? ' in-fullscreen' : '')} ref={headerRef}>
            <div className="header-container">
                <div
                    className="hide-in-mobile side-panel-icon"
                    onClick={() => !isEndNode && !isStartNode && toggleSidebar()}
                >
                    <ConditionalComponent if={!isStartNode && !!isRegularProcedure}>
                        <img
                            src={sidebar ? SidebarOpenIcon : SidebarCloseIcon}
                            alt="slides icon"
                        />
                    </ConditionalComponent>
                </div>

                <div
                    className={`procedure-title ${isIOS && isTablet && generalStore.isInFullscreen ? 'ipad' : ''} ${(procedure.ProcedureType === procedureTypeEnum.FUSION || procedureStore.runProcedureIn3D) ? 'digital-twin' : ''}`}>
                    {renderBreadCrumbs(procedure)}
                </div>
            </div>
            <div className={`procedure-options-header${isPreview ? " preview" : ""}`}>
                <ConditionalComponent if={!!isRegularProcedure}>

                    <ConditionalComponent if={!isStartNode && !isEndNode}>
                        <Button
                            className={'show-text-button hide-in-tablet hide-in-mobile' + (isPreview ? ' preview' : '')}
                            variant='outlined'
                            onClick={toggleSupportingText}
                        >
                            <TextFieldsIcon htmlColor='white'/>
                            &nbsp;
                            {!procedureStore.showSupportingText ? generalStore.translate('showSupportingText') : generalStore.translate('hideSupportingText')}
                        </Button>
                        <IconButton onClick={toggleSupportingText} className='show-in-tablet show-in-mobile'>
                            <TextFieldsIcon htmlColor='white'/>

                        </IconButton>
                    </ConditionalComponent>

                    <ConditionalComponent if={availableLanguages.length > 0 && !procedureStore.runProcedureIn3D}>
                        <Select
                            variant="outlined"
                            labelId="select-language-label"
                            className="select-language"
                            value={userSelectedLanguage || procedure.DefaultLanguage || languageEnum.ENGLISH_US}
                            label={generalStore.translate('editIn')}
                            onChange={handleChangeSelectedLanguage}
                            MenuProps={{
                                disablePortal: generalStore.isInFullscreen || !isPreview,
                                PaperProps: {
                                    style: {
                                        marginLeft: generalStore.isInFullscreen ? '6px' : '0px',
                                    },
                                }
                            }}
                        >
                            {availableLanguages.map((lang, i) => Utility.renderLanguageMenuOption(lang, i, procedure.DefaultLanguage, false))}
                        </Select>
                    </ConditionalComponent>

                </ConditionalComponent>
            </div>
            <ConditionalComponent if={!!withFullscreen && !isIphone}>
                <Button
                    className={`${procedure.ProcedureType === procedureTypeEnum.FUSION ? 'fullscreen-digital-twin' : 'fullscreen'}`}
                    onClick={() => Utility.toggleFullScreen(fullscreenRefElement)}>
                    {!generalStore.isInFullscreen ? <FullscreenIcon/> : <FullscreenExitIcon/>}
                </Button>
            </ConditionalComponent>
        </div>
    )
});

export default ProcedureHeader
